import { useState, useEffect } from 'react';
import { Download, Share2, X } from 'lucide-react';

export function FloatingInstallShare() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIos(ios);
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (isStandalone || dismissed) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDismissed(true);
      setDeferredPrompt(null);
    } else if (isIos) {
      setShowMenu('ios');
    }
    setShowMenu(false);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'FendanaCura',
      text: 'Smart care, seamlessly managed. Join FendanaCura - the all-in-one care agency platform.',
      url: window.location.origin,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert('Link copied to clipboard!');
      }
    } catch (err) { /* user cancelled */ }
    setShowMenu(false);
  };

  return (
    <>
      {/* iOS Install Instructions Modal */}
      {showMenu === 'ios' && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowMenu(false)}>
          <div className="glass-card rounded-2xl p-6 max-w-sm w-full mb-8 text-center" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-heading font-bold text-foreground mb-2">Install FendanaCura</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Tap the <span className="inline-flex items-center px-1"><Share2 className="w-4 h-4" /></span> share button in Safari, then scroll down and tap <strong>"Add to Home Screen"</strong>
            </p>
            <button onClick={() => setShowMenu(false)} className="btn-primary px-6 py-2 rounded-full text-sm">Got it</button>
          </div>
        </div>
      )}

      {/* Floating Button Group */}
      <div className="fixed bottom-24 lg:bottom-6 right-6 z-40 flex flex-col items-center gap-3" data-testid="floating-install-share">
        {/* Expanded menu */}
        {showMenu === true && (
          <div className="flex flex-col gap-2 mb-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
            <button
              onClick={handleInstall}
              className="flex items-center gap-2 px-5 py-3 glass-card rounded-full text-sm font-medium text-foreground hover:bg-white/10 dark:hover:bg-white/5 transition-all shadow-lg"
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4 text-[#00A6A0]" />
              <span>Install App</span>
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-5 py-3 glass-card rounded-full text-sm font-medium text-foreground hover:bg-white/10 dark:hover:bg-white/5 transition-all shadow-lg"
              data-testid="share-app-btn"
            >
              <Share2 className="w-4 h-4 text-[#F97316]" />
              <span>Share App</span>
            </button>
          </div>
        )}

        {/* Main floating ring button */}
        <button
          onClick={() => setShowMenu(prev => prev === true ? false : true)}
          className="install-ring w-14 h-14 rounded-full bg-gradient-to-br from-[#00A6A0] to-[#008A85] flex items-center justify-center shadow-2xl transition-transform duration-300 hover:scale-110 active:scale-95"
          data-testid="floating-install-toggle"
          aria-label="Install or share app"
        >
          {showMenu === true ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Download className="w-6 h-6 text-white" />
          )}
        </button>
      </div>
    </>
  );
}

export default FloatingInstallShare;
